import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { categoryMap } from '@/mocks/content';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (searchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [searchOpen]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchOpen(false);
      setSearchQuery('');
    }
  };

  const closeAll = () => {
    setMobileMenuOpen(false);
    setSearchOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <nav className="w-full max-w-[1400px] mx-auto px-4 md:px-6 flex items-center justify-between h-16" aria-label="主导航">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 cursor-pointer" onClick={closeAll}>
          <div className="w-9 h-9 flex items-center justify-center bg-primary-500 rounded-lg">
            <i className="ri-movie-2-line text-background-50 text-lg" />
          </div>
          <span className={`font-heading text-xl font-bold transition-colors duration-300 ${
            scrolled ? 'text-foreground-900' : 'text-white'
          }`}>
            色影视
          </span>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-8">
          <Link
            to="/"
            className={`text-sm font-medium transition-colors duration-200 hover:text-primary-500 ${
              scrolled ? 'text-foreground-700' : 'text-white/90'
            }`}
          >
            首页
          </Link>
          {Object.entries(categoryMap).map(([key, cat]) => (
            <Link
              key={key}
              to={`/category/${cat.slug}`}
              className={`text-sm font-medium transition-colors duration-200 hover:text-primary-500 ${
                scrolled ? 'text-foreground-700' : 'text-white/90'
              }`}
            >
              {cat.label}
            </Link>
          ))}
          <Link
            to="/about"
            className={`text-sm font-medium transition-colors duration-200 hover:text-primary-500 ${
              scrolled ? 'text-foreground-700' : 'text-white/90'
            }`}
          >
            关于我们
          </Link>
        </div>

        {/* Desktop actions */}
        <div className="hidden md:flex items-center gap-3">
          {searchOpen ? (
            <form onSubmit={handleSearch} className="flex items-center">
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="搜索影片..."
                className="w-48 px-3 py-1.5 text-sm rounded-lg bg-background-100 border border-background-200 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                onBlur={() => { if (!searchQuery) setSearchOpen(false); }}
              />
            </form>
          ) : (
            <button
              onClick={() => setSearchOpen(true)}
              className={`w-9 h-9 flex items-center justify-center rounded-lg transition-colors duration-200 cursor-pointer ${
                scrolled ? 'hover:bg-background-100 text-foreground-600' : 'hover:bg-white/10 text-white/90'
              }`}
              aria-label="搜索"
            >
              <i className="ri-search-line" />
            </button>
          )}
        </div>

        {/* Mobile hamburger */}
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className={`md:hidden w-9 h-9 flex items-center justify-center rounded-lg transition-colors duration-200 cursor-pointer ${
            scrolled ? 'text-foreground-700' : 'text-white'
          }`}
          aria-label="菜单"
        >
          <i className={mobileMenuOpen ? 'ri-close-line text-xl' : 'ri-menu-line text-xl'} />
        </button>
      </nav>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-b border-background-200/70">
          <div className="px-4 py-4 flex flex-col gap-3">
            <Link to="/" onClick={closeAll} className="text-sm font-medium text-foreground-700 py-2">首页</Link>
            {Object.entries(categoryMap).map(([key, cat]) => (
              <Link
                key={key}
                to={`/category/${cat.slug}`}
                onClick={closeAll}
                className="text-sm font-medium text-foreground-700 py-2"
              >
                {cat.label}
              </Link>
            ))}
            <Link to="/about" onClick={closeAll} className="text-sm font-medium text-foreground-700 py-2">关于我们</Link>
            <form onSubmit={handleSearch} className="mt-2">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="搜索影片..."
                  className="flex-1 px-3 py-2 text-sm rounded-lg bg-background-100 border border-background-200 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300"
                />
                <button type="submit" className="px-4 py-2 bg-primary-500 text-background-50 text-sm rounded-lg whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors">
                  搜索
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </header>
  );
}